import React, { useEffect, useState, useRef } from 'react';
import BoundedGoban from './shudan/BoundedGoban';
import Board from "@sabaki/go-board";
import {parseCompressedVertices} from '@sabaki/sgf'
import { HORIZONTALCOORDS } from './App.js'
import { parseVertex } from '@sabaki/sgf'

const GameCard = ({ game, onClick, setShowGameInfoWindow, setFocusedGame }) => {

  const emptyBoard = Board.fromDimensions(19); // empty board
  const [board, setBoard] = useState(emptyBoard); 
  const [currentMoveNumber, setCurrentMoveNumber] = useState(0)
  const [state, setState] = useState(game.state)
  const [hovered, setHovered] = useState(false);
  const timeoutRef = useRef(null);

  const hoveredRef = useRef(hovered); 

  useEffect(() => {
    hoveredRef.current = hovered;
  }, [hovered]);

  const currentMoveNumberRef = useRef(currentMoveNumber); 

  useEffect(() => {
    currentMoveNumberRef.current = currentMoveNumber;
  }, [currentMoveNumber]);

  useEffect(() => {
    redrawThenGoToMove()
  },[])

  // Redraws entire board up to a specified move number
  const redrawThenGoToMove = () => {

    // console.debug("redrawThenGoToMove() called")
    let {gameTrees, gameIndex, gameCurrents} = state
    let tree = gameTrees[gameIndex]
    let currents = gameCurrents[gameIndex]
    const dimensions = game.info.size

    // Check for initial/handicap stones 
    let moves = []
    let initialStones = []
    let initialMoves = []
    let initialPlayer = null
    let rootNode = tree.root

    for (let prop of ['B', 'W', 'AB', 'AW']) {
      if (rootNode.data[prop] == null)
        continue
      let color = prop.slice(-1)
      let sign = color === 'B' ? 1 : -1
      let vertices = [].concat(
        ...rootNode.data[prop].map(parseCompressedVertices)
      )

      for (let vertex of vertices) {
        // Convert [2,4] -> "C15" for Katago 
        const gtpVertex=HORIZONTALCOORDS[vertex[0]]+String(dimensions[0] - vertex[1])
        const move = [color,gtpVertex]
        if (prop[0] === 'A') {
          initialStones.push(move)
          initialMoves.push([sign,vertex])
        }
        else {
          moves.push(move)  
        }
      }
    }

    let newBoard = Board.fromDimensions(dimensions[0], dimensions[1])

    // Play out the initial stones 
    for (let move of initialMoves) {
        newBoard = newBoard.makeMove(move[0], move[1])
      }

    setBoard(newBoard)
    }

  const goToNextMove = (i, oldBoard) => {

    let {gameTrees, gameIndex, gameCurrents} = state
    let tree = gameTrees[gameIndex]
    let currents = gameCurrents[gameIndex]
    const dimensions = game.info.size

      // if (currentMoveNumber < tree.listNodesVertically(tree.root.id,1,gameCurrents)-1) {
      try {
        let node = tree.navigate(
          i,
          1, // go forward 1 step
          gameCurrents[gameIndex]
        )

        // Make move
        // TODO: implement errors for invalid moves (see board.makeMove on @sabaki/go-board)
        let propData = {B: 1, W: -1}
        let newBoard = null
        let vertex = null

        for (let prop in propData) {
          if (node.data[prop] == null) continue

          vertex = parseVertex(node.data[prop][0])
          newBoard = oldBoard.makeMove(
            propData[prop], 
            vertex)
          newBoard.currentVertex = vertex

          break
        }
        return newBoard

      } catch (err) { // No more (valid) moves

        return null
      }
  }

  const handleMouseEnter = () => {
    // console.debug("mouse is over")
    if(! hoveredRef.current) {
      setHovered(true);
      runWithIncreasingSpeed(100, 100, 0.999); // Example: 10 iterations, start at 1s, accelerate by 10%
    }
  };

  const handleMouseLeave = () => {
    setHovered(false);
    clearTimeout(timeoutRef.current); // Stop any ongoing timeouts
    setCurrentMoveNumber(0)
    setBoard(emptyBoard)
    redrawThenGoToMove()
  };

  useEffect(() => {
    const handleGlobalMouseMove = (event) => {
      if (hoveredRef.current) {
        const rect = event.target.closest('.game-card')?.getBoundingClientRect();
        if (!rect || event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) {
          handleMouseLeave();
        }
      }
    };
  
    document.addEventListener('mousemove', handleGlobalMouseMove);
  
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, []);
  

  function runWithIncreasingSpeed(iterations, initialInterval, accelerationFactor) {
    let currentIteration = 0;
    let currentInterval = initialInterval;
    let oldBoard = board
    hoveredRef.current = true
  
    function executeNext() {
      if (currentIteration >= iterations) return; // Stop after specified iterations
  
      const newBoard = goToNextMove(currentIteration, oldBoard); // Call the function

      if(! !!newBoard || !hoveredRef.current) { // No more moves
        clearTimeout(timeoutRef.current); // Stop any ongoing timeouts
        return
      }

      oldBoard = newBoard

      setBoard(newBoard)
      setCurrentMoveNumber(currentIteration+1)

      // // Reduce the interval for the next iteration
      currentInterval = initialInterval * (1/3 + (2/3) * accelerationFactor**currentIteration);

      currentIteration++;
  
      // Schedule the next execution with a shorter interval
      setTimeout(executeNext, currentInterval);

    }
  
    // Start the first execution
    executeNext();
  }

  function handleGameInfoClick(e) {
    setShowGameInfoWindow(true)
    setFocusedGame(game)
  }

    // let {
  //     playerNames,
  //     playerRanks,
  //     blackName,
  //     blackRank,
  //     whiteName,
  //     whiteRank,
  //     gameName,
  //     eventName,
  //     gameComment,
  //     date,
  //     result,
  //     komi,
  //     handicap,
  //     size, 
  //     gameId, 
  //     gameLength, 
  //   } = game; 

  // Here we format game fields depending on what data was provided in game.info:
  let gameName = null
  let blackPlayer = 'Unknown'
  let whitePlayer = 'Unknown'
  let gameDetails = game.info.eventName
  let blackRank = game.info.blackRank ? ' ' + game.info.blackRank : ''
  let whiteRank = game.info.whiteRank ? ' ' + game.info.whiteRank : ''

  if(!!game.info.blackName)
    blackPlayer = game.info.blackName

  if(!!game.info.whiteName)
    whitePlayer = game.info.whiteName

  const altGameName = gameName = blackPlayer + blackRank + ' vs. ' + whitePlayer + whiteRank

  // console.log(game.info)

  return (
    <div 
      className="game-card"
      onMouseEnter={handleMouseEnter}>
      {!!game.info && 
      <div>
        <h3 className="game-players">
          {altGameName}
        </h3>
        <div className="game-preview">
          <div 
            className="goban-container">
            <div onClick = {onClick}>
            <BoundedGoban
              className="goban"
              style={{
                '--shudan-board-border-color': '#CA933A',
                '--shudan-board-background-image': 'url(./board.png)',
                '--shudan-board-background-color': '#F1B458',
                cursor: "pointer"
              }}
              maxWidth={300}
              maxHeight={300}
              board={board}
              signMap={board.signMap}
              currentMoveNumber={currentMoveNumber}
              />
            </div>
          </div>
        </div>
        <div className="footer">
          <div style="grid-column: 2; justify-self: center;">
            <h3 className="game-result">{game.info.result}</h3>
            {!! game.info.gameName && <h4 className="game-title">{game.info.gameName}</h4>}
          </div>
          <img 
            src="/icons/info.svg" 
            className="info" 
            onClick={handleGameInfoClick}
            alt="Game info" 
            style="grid-column: 3; justify-self: end;" />
        </div>
      </div>
      }
    </div>
  );
};

export default GameCard;